import React, { FC, useEffect, useState } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import Stack from '../stack/Stack';
import Typography from '../typography/Typography';
import { formatForID, useBackgroundColor } from '../../utils';
import LinkList from '../linkList/LinkList';
import Html from '../html/Html';
import { LinkType } from '../../types';

// Interface

export interface OnPageCalulatorProps {
  heading: string;
  subHeading?: string;
  backgroundColour: ThemeColorsKeys;
  children: React.ReactNode;
  smallPrintText?: string;
  additionalLinksHeading?: string;
  additionalLinks?: LinkType[];
}

interface StyledOnPageCalulatorProps {
  backgroundColour: ThemeColorsKeys;
}

// Styles

const StyledOnPageCalulator = styled.div<StyledOnPageCalulatorProps>`
  ${({ theme: { space, colors, media, borderRadii }, backgroundColour }) => css`
    ${useBackgroundColor(backgroundColour)}
    padding: ${space.fluidSmallToLarge};
    border: solid 1px ${colors.darkBlue};
    border-radius: ${borderRadii.small};
  `};
`;

// JSX

export const OnPageCalculator: FC<OnPageCalulatorProps> = ({
  heading,
  subHeading,
  backgroundColour,
  smallPrintText,
  additionalLinksHeading,
  additionalLinks,
  children,
}) => (
  <Stack gap="fluidLargeToXxLarge" id={heading ? formatForID(heading) : ""}>
    <StyledOnPageCalulator backgroundColour={backgroundColour}>
      <Stack gap="fluidSmallToMedium">
        <Stack gap="small">
          <Typography component="h2" variant="headingMedium">
            {heading}
          </Typography>
          {subHeading && (
            <Typography component="p" variant="secondaryHeadingSmall">
              {subHeading}
            </Typography>
          )}
        </Stack>
        {children}
        {smallPrintText && <Html small>{smallPrintText}</Html>}
      </Stack>
    </StyledOnPageCalulator>
    {additionalLinksHeading || additionalLinks ? (
      <Stack gap="small">
        {additionalLinksHeading && (
          <Typography component="h3" variant="bodyLargeBold">
            {additionalLinksHeading}
          </Typography>
        )}

        {additionalLinks && <LinkList items={additionalLinks} />}
      </Stack>
    ) : (
      ''
    )}
  </Stack>
);

export default OnPageCalculator;
